/**
 * AIS disruption layer — WM's white-haloed anomaly markers, the largest
 * maritime sign on screen (DeckGLMap.ts:3727-3750). Fed by the AIS anomaly
 * detector (analysis:ais-anomaly), not raw vessel positions: spoofing-class
 * anomalies (impossible speed, position jumps) render red, signal gaps
 * (dark zones) orange. Dark-ship anomalies stay on the dark-ship layer.
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { AISAnomaly } from '@/intelligence/analysis/ais-spoofing';
import { eventBus } from '@/core/events';

/** Layer IDs managed by this renderer. */
export const AIS_DISRUPTION_LAYER_IDS = new Set(['ais-disruption']);

/**
 * Anomaly types this layer renders. Dark anomalies (dark-ship AND
 * dark-zone) belong to dark-ship-layer — rendering dark-zone here too
 * would draw the same event twice with conflicting color semantics.
 */
const DISRUPTION_TYPES = new Set(['impossible-speed', 'position-jump']);

const MAX_CACHED = 500;
const CACHE_TTL_MS = 2 * 60 * 60 * 1000;

const disruptionCache = new Map<string, AISAnomaly>();
let cleanupFn: (() => void) | null = null;

/** Subscribe to AIS anomaly events. */
export function initAisDisruptionLayer(): void {
  if (cleanupFn) return;

  cleanupFn = eventBus.on('analysis:ais-anomaly', (anomaly) => {
    if (!DISRUPTION_TYPES.has(anomaly.anomalyType)) return;
    disruptionCache.set(`${anomaly.mmsi}-${anomaly.anomalyType}`, anomaly);

    if (disruptionCache.size > MAX_CACHED) {
      const now = Date.now();
      for (const [key, a] of disruptionCache) {
        if (now - new Date(a.detectedAt).getTime() > CACHE_TTL_MS) {
          disruptionCache.delete(key);
        }
      }
    }
  });
}

/** Tear down the disruption layer event listener. */
export function destroyAisDisruptionLayer(): void {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = null;
  }
  disruptionCache.clear();
}

/**
 * WM's 3-tier fill (DeckGLMap.ts:3734-3744): spoofing/high -> red,
 * medium -> orange, else yellow. WM's feed carries explicit severity;
 * Sentinel derives the tier from the anomaly type: spoofing-class
 * anomalies are the high-value signal, signal gaps the medium one.
 */
export function getDisruptionColor(anomaly: AISAnomaly): [number, number, number, number] {
  if (anomaly.anomalyType === 'impossible-speed' || anomaly.anomalyType === 'position-jump') {
    return [255, 50, 50, 220];
  }
  if (anomaly.anomalyType === 'dark-zone') {
    return [255, 150, 0, 200];
  }
  return [255, 200, 100, 180];
}

/** Create the AIS disruption layer (WM DeckGLMap.ts:3727-3750, verbatim values). */
export function createAisDisruptionLayers(): Layer[] {
  const anomalies = Array.from(disruptionCache.values());
  if (anomalies.length === 0) return [];

  return [new ScatterplotLayer<AISAnomaly>({
    id: 'ais-disruptions-layer',
    data: anomalies,
    pickable: true,
    filled: true,
    stroked: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getRadius: 12000,
    getFillColor: (d) => getDisruptionColor(d),
    // The white halo is the distinguishing sign against density dots beneath
    getLineColor: [255, 255, 255, 150],
    lineWidthMinPixels: 1,
    radiusMinPixels: 6,
    radiusMaxPixels: 14,
    updateTriggers: {
      getFillColor: [anomalies.length],
    },
  })];
}
